/**
 * Store for Create Administrators troubleshooting page.
 * Holds state and business logic for bulk-creating platform administrators.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { capitalizeWords, validateEmail } from '../../../../../services/helpers';
import { IHttpClientError } from '../../../../../services/api/http-client-error.types';
import TroubleshootingService, {
    ICreateAdministratorUser,
    ICreateAdministratorsResult,
} from '../../../../../services/api/troubleshooting-service';
import { TYPE, rootContainer } from '../../../../../services/container/root';

function parseEmailToUser(email: string): ICreateAdministratorUser | null {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
        return null;
    }
    const [userName] = trimmedEmail.split('@');
    const nameSegments = userName.split('.').filter((s) => s.length > 0);
    if (nameSegments.length === 0) {
        return null;
    }
    const capitalized = nameSegments.map((s) => capitalizeWords(s));
    const displayName = capitalized.join(' ');
    const lastName = capitalized[capitalized.length - 1];
    const firstName = capitalized.length > 1 ? capitalized.slice(0, -1).join(' ') : capitalized[0];

    return {
        email: trimmedEmail,
        first_name: firstName,
        last_name: lastName,
        display_name: displayName,
        viewer: true,
        editor: true,
    };
}

class CreateAdministratorsStore {
    private _emailInput = '';
    private _users: ICreateAdministratorUser[] = [];
    private _isSubmitting = false;
    private _result: ICreateAdministratorsResult | null = null;
    private _errorMessage: string | null = null;
    /** Raw comma-separated segments that failed validation or name parsing on last Parse */
    private _invalidParseEntries: string[] = [];
    /** Emails skipped on last Parse because they were already listed earlier in the input */
    private _duplicateParseEntries: string[] = [];

    constructor() {
        makeAutoObservable(this);
    }
    
    private get service(): TroubleshootingService {
        return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
    }

    get emailInput(): string {
        return this._emailInput;
    }

    get users(): ICreateAdministratorUser[] {
        return this._users;
    }

    get isSubmitting(): boolean {
        return this._isSubmitting;
    }

    get result(): ICreateAdministratorsResult | null {
        return this._result;
    }

    get errorMessage(): string | null {
        return this._errorMessage;
    }

    get invalidParseEntries(): string[] {
        return this._invalidParseEntries;
    }

    get duplicateParseEntries(): string[] {
        return this._duplicateParseEntries;
    }

    setEmailInput(value: string): void {
        this._emailInput = value;
        this._invalidParseEntries = [];
        this._duplicateParseEntries = [];
    }

    parseEmails(): void {
        this._errorMessage = null;
        this._result = null;
        this._invalidParseEntries = [];
        this._duplicateParseEntries = [];

        const trimmedCommaSeparatedSegments = this._emailInput
            .split(',')
            .map((rawSegment) => rawSegment.trim())
            .filter(Boolean);
        const parsedAdministrators: ICreateAdministratorUser[] = [];
        const invalidRawSegments: string[] = [];
        const duplicateAdministratorEmails: string[] = [];
        const normalizedEmailsSeenInInput = new Set<string>();

        for (const inputSegment of trimmedCommaSeparatedSegments) {
            const userDataToCreate = parseEmailToUser(inputSegment);
            if (!userDataToCreate) {
                invalidRawSegments.push(inputSegment);
                continue;
            }
            const normalizedEmail = userDataToCreate.email.toLowerCase();
            if (normalizedEmailsSeenInInput.has(normalizedEmail)) {
                duplicateAdministratorEmails.push(userDataToCreate.email);
                continue;
            }
            normalizedEmailsSeenInInput.add(normalizedEmail);
            parsedAdministrators.push(userDataToCreate);
        }
        this._invalidParseEntries = invalidRawSegments;
        this._duplicateParseEntries = duplicateAdministratorEmails;
        this._users = parsedAdministrators;
    }

    updateUser(rowIndex: number, field: keyof ICreateAdministratorUser, value: string | boolean): void {
        if (rowIndex < 0 || rowIndex >= this._users.length) {
            return;
        }
        this._users = this._users.map((existingRow, currentRowIndex) =>
            currentRowIndex === rowIndex ? { ...existingRow, [field]: value } : existingRow
        );
    }

    removeUser(rowIndex: number): void {
        this._users = this._users.filter((_userRow, currentRowIndex) => currentRowIndex !== rowIndex);
    }

    private getValidUsers(): ICreateAdministratorUser[] {
        return this._users.filter((userRow) => {
            const emailOk = validateEmail(userRow.email);
            const hasRole = userRow.viewer || userRow.editor;
            return (
                emailOk &&
                userRow.first_name?.trim() &&
                userRow.last_name?.trim() &&
                userRow.display_name?.trim() &&
                hasRole
            );
        });
    }

    async submit(): Promise<void> {
        this._errorMessage = null;
        this._result = null;

        const validUsers = this.getValidUsers();
        if (validUsers.length === 0) {
            this._errorMessage = 'No valid users to create. Ensure all emails are valid and at least one role is selected per user.';
            return;
        }

        this._isSubmitting = true;
        try {
            const response = await this.service.createAdministrators(validUsers);
            const resultPayload = (response as { data?: ICreateAdministratorsResult }).data;
            runInAction(() => {
                if (resultPayload) {
                    this._result = resultPayload;
                }
            });
        } catch (err: unknown) {
            const error = err as IHttpClientError;
            const httpStatus = error?.response?.status;
            const errorResponseBody = error?.response?.data;
            runInAction(() => {
                if (httpStatus === 403) {
                    this._errorMessage = 'You are not authorized to use this tool.';
                } else if (httpStatus === 422 && errorResponseBody?.errors) {
                    this._errorMessage = Object.values(errorResponseBody.errors).flat().join(' ');
                } else {
                    this._errorMessage =
                        errorResponseBody?.error || error?.message || 'An error occurred. Please try again.';
                }
            });
        } finally {
            runInAction(() => {
                this._isSubmitting = false;
            });
        }
    }
}

export default new CreateAdministratorsStore();
